/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  CommentOutlined,
  InfoCircleFilled,
  SmileOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Card,
  Col,
  List,
  Modal,
  notification,
  Row,
  Spin,
  Table,
  Tooltip,
} from "antd";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import Emoji from "react-emoji-render";

import { getPopularThreads } from "../../lib/discordQueries";
import { getPopularPosts } from "../../lib/queries";
import { useEndTime, useStartTime } from "../DateContext/DateContext";

function usePopularPosts(
  startTime,
  endTime,
  teamId,
  platform,
  excludedChannels
) {
  const [loading, setLoading] = useState(true);
  const [popularPosts, setPopularPosts] = useState([]);

  const workspaceId =
    JSON.parse(localStorage.getItem("supabase.auth.token") || "{}")
      ?.currentSession.user?.app_metadata.workspace || "";

  // search when time changes
  useEffect(() => {
    // map results for messages
    const mapResults = (results) => {
      results = results.map((result) => {
        result.key = result.permalink;
        result.channelName = `#${result.channel.name}`;
        result.userAdmin = result.slack_user ? result.slack_user.is_admin : "";
        result.posted = moment(result.formatted_ts).format("MMM D");
        return result;
      });
      return results;
    };

    // map results for Discord threads
    const mapThreads = (threads) => {
      threads = threads.map((thread) => {
        thread.key = thread.id;
        thread.channelName = `#${thread.channel}`;
        thread.posted = moment(thread.created_at).format("MMM D");
        return thread;
      });
      return threads;
    };

    if (platform === "slack") {
      const loadPopular = async () => {
        setLoading(true);
        try {
          const data = await getPopularPosts(
            startTime,
            endTime,
            teamId,
            null,
            excludedChannels ?? []
          );
          if (data) {
            const displayResults = mapResults(data);
            setPopularPosts(displayResults);
          }
          return data;
        } finally {
          setLoading(false);
        }
      };
      loadPopular();
    } else {
      const loadThreads = async () => {
        setLoading(true);
        try {
          const data = await getPopularThreads(
            startTime,
            endTime,
            teamId,
            workspaceId,
            null,
            excludedChannels ?? []
          );
          if (data) {
            const displayResults = mapThreads(data);
            setPopularPosts(displayResults);
          }
          return data;
        } finally {
          setLoading(false);
        }
      };
      loadThreads();
    }
  }, [startTime, endTime, teamId, excludedChannels]);

  if (!workspaceId) {
    return {
      data: [],
      isLoading: false,
    };
  }

  return { data: popularPosts, isLoading: loading };
}

function useClipboard() {
  const copyToClipboard = useCallback(async (element) => {
    /*
      Use navigator.clipboard.write as a fallback of document.execCommand('copy'). This is because:
      1. document.execCommand is deprecated and will be removed in the future.
      2. Clipboard.write, which is used for copying HTML, isn't yet widely supported (Firefox for example). 
      3. In terms of copying as plain text, execCommand actually works better. 
      
      References
      - https://developer.mozilla.org/en-US/docs/Web/API/document/execCommand
      - https://fcqoaycj5axlfhm2klwe4ymk4g3zpdlxzhkgizb63ot6dd27vzmq.arweave.net/KKDgYEnoLrKdmlLsTmGK4beXjXfJ1GRkPtun4Y9frlk. Note this page is a snapshot of MDN page in case it's updated in the future: https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/Interact_with_the_clipboard
      */
    try {
      const parent = document.createElement("div");
      parent.style.height = 0; // slightly less hacky than setting display to inline and none. :)
      parent.appendChild(element);
      document.body.appendChild(parent);
      window.getSelection().removeAllRanges();
      const range = document.createRange();
      range.selectNode(
        typeof element === "string" ? document.getElementById(element) : element
      );
      window.getSelection().addRange(range);
      document.execCommand("copy");
      parent.remove();
      window.getSelection().removeAllRanges();
    } catch (error) {
      // We might want to process permissions properly if we need to use clipboard outside of the active tab: https://developer.mozilla.org/en-US/docs/Web/API/Clipboard/write#browser____compatibility
      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/html": new Blob([element.outerHTML], { type: "text/html" }),
          // TODO When Clipboard.write becomes more mainstream, we should revisit this to apply proper formatting to plain text. Right it just concatenates all the text without line breaks.
          "text/plain": new Blob([element.textContent], { type: "text/plain" }),
        }),
      ]);
    }
  }, []);
  return { copyToClipboard };
}

/**
 *
 * props: startTime, endTime and optional excludedChannels. If not provided, it doesn't exclude channels
 *
 */
const TopPosts = (props) => {
  const [isPopularModalVisible, setPopularModalVisible] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const { startTime, setStartTime } = useStartTime();
  const { endTime, setendTime } = useEndTime();
  const teamId =
    props.platform === "slack"
      ? localStorage.getItem("team-id")
      : localStorage.getItem("guild-id");

  const { data: popularPosts, isLoading: loading } = usePopularPosts(
    startTime,
    endTime,
    teamId,
    props.platform,
    props.excludedChannels
  );

  const openNotificationWithIcon = (type) => {
    notification[type]({
      message: "Copied to clipboard!",
    });
  };

  const { copyToClipboard } = useClipboard();

  const copyThreads = async () => {
    const element = document.createElement("ul");
    const items = popularPosts.map((post) => {
      const li = document.createElement("li");
      li.innerHTML =
        `<p> <strong> ${post.name} </strong> in <i>${post.channelName}</i></p>` +
        `<p>(${post.thread_count} ${
          post.thread_count > 1 ? "threads" : "thread"
        } | ${post.member_count} ${post.member_count > 1 ? "posters" : "poster"}
        )</p>`;
      return li;
    });
    items.forEach((item) => element.appendChild(item));
    await copyToClipboard(element);
    openNotificationWithIcon("success");
  };

  const copyMessages = async () => {
    // Prepare the data to be copied
    const element = document.createElement("ul");
    const selectedPostLinks = new Set(selectedMessages);
    const selectedPosts = popularPosts.filter((p) =>
      selectedPostLinks.has(p.permalink)
    );
    const items = selectedPosts.map((post) => {
      const li = document.createElement("li");
      li.innerHTML =
        `<p> <strong> ${
          post.slack_user ? post.slack_user.name : null
        } </strong> in <i>${post.channelName}</i></p>` +
        `<p><a href="${post.permalink}">${post.text}</a></p>` +
        `<p>(${post.reply_count} ${
          post.reply_count > 1 ? "replies" : "reply"
        } | ${post.reaction_count} ${
          post.reaction_count > 1 ? "reactions" : "reaction"
        })</p>`;
      return li;
    });
    items.forEach((item) => element.appendChild(item));

    await copyToClipboard(element);
    openNotificationWithIcon("success");
  };

  const onSelectChange = async (selectedRowKeys, selectedRows) => {
    setSelectedMessages(selectedRowKeys.slice(0, 100));
  };

  const rowSelection = {
    selectedRowKeys: selectedMessages,
    onChange: onSelectChange,
  };

  const hasMessagesSelected = selectedMessages.length > 0;

  const showPopularModal = () => {
    window.analytics.track("View more messages clicked");
    setPopularModalVisible(true);
  };

  const hidePopularModal = () => {
    setPopularModalVisible(false);
  };

  const getChannelFilters = (posts) => {
    const filterValues = new Set();
    // eslint-disable-next-line no-restricted-syntax
    for (const post of posts) {
      filterValues.add(post.channelName);
    }
    const filters = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const value of filterValues) {
      filters.push({ text: value, value });
    }
    return filters;
  };

  const popularMessagesColumns = [
    {
      title: "Message",
      dataIndex: "text",
      key: "text",
      render: (text, record) => {
        let image32 = "";
        let userName = "";
        if (record.slack_user) {
          image32 = record.slack_user.image_32;
          userName = record.slack_user.name;
        }
        return (
          <div>
            <Row>
              <Col span={2}>
                <Avatar src={image32} />
              </Col>
              <Col span={22}>
                <Row>
                  <span style={{ color: "grey", marginRight: "10px" }}>
                    {userName} hi
                  </span>
                  <span style={{ color: "grey", marginRight: "10px" }}>
                    | {record.posted}
                  </span>
                  <Tooltip title="Click to open in slack">
                    <a href={record.permalink} target="_blank" rel="noreferrer">
                      <img alt="link" width="15" src="/external-link.svg" />
                    </a>
                  </Tooltip>
                </Row>
                <Row>
                  <Emoji text={record.text} />
                </Row>
                <Row>
                  <span style={{ color: "grey", marginRight: "10px" }}>
                    {record.reaction_count} reactions | {record.reply_count}{" "}
                    replies{" "}
                  </span>
                </Row>
              </Col>
            </Row>
          </div>
        );
      },
      filters: [
        { text: "Admin", value: true },
        { text: "Non-admin", value: false },
      ],
      onFilter: (value, record) => record.userAdmin === value,
    },
    {
      title: "Total Replies",
      dataIndex: "reply_count",
      sorter: (a, b) => {
        return a.reply_count - b.reply_count;
      },
      sortDirections: ["descend", "ascend"],
      width: "10%",
    },
    {
      title: "Unique Posters",
      dataIndex: "reply_users",
      sorter: (a, b) => {
        return a.reply_users - b.reply_users;
      },
      sortDirections: ["descend", "ascend"],
      width: "10%",
    },
    {
      title: "Channel",
      dataIndex: "channelName",
      filters: getChannelFilters(popularPosts),
      onFilter: (value, record) => record.channelName.indexOf(value) === 0,
      sorter: (a, b) => {
        if (a.channelName < b.channelName) {
          return -1;
        }
        if (a.channelName > b.channelName) {
          return 1;
        }
        return 0;
      },
      sortDirections: ["descend", "ascend"],
      width: "20%",
    },
  ];

  const popularThreadsColumns = [
    {
      title: "Thread",
      dataIndex: "name",
      key: "name",
      render: (name, record) => {
        return (
          <div>
            <Row>
              <Col span={22}>
                <Row>
                  <span style={{ color: "grey", marginRight: "10px" }}>
                    {record.posted}
                  </span>
                </Row>
                <Row>{record.name}</Row>
                <Row>
                  <span style={{ color: "grey", marginRight: "10px" }}>
                    {record.thread_count} messages | {record.member_count}{" "}
                    posters{" "}
                  </span>
                </Row>
              </Col>
            </Row>
          </div>
        );
      },
    },
    {
      title: "Messages",
      dataIndex: "thread_count",
      sorter: (a, b) => {
        return a.thread_count - b.thread_count;
      },
      sortDirections: ["descend", "ascend"],
      width: "15%",
    },
    {
      title: "Unique Posters",
      dataIndex: "member_count",
      sorter: (a, b) => {
        return a.member_count - b.member_count;
      },
      sortDirections: ["descend", "ascend"],
      width: "15%",
    },
    {
      title: "Channel",
      dataIndex: "channelName",
      filters: getChannelFilters(popularPosts),
      onFilter: (value, record) => record.channelName.indexOf(value) === 0,
      sorter: (a, b) => {
        if (a.channelName < b.channelName) {
          return -1;
        }
        if (a.channelName > b.channelName) {
          return 1;
        }
        return 0;
      },
      sortDirections: ["descend", "ascend"],
      width: "20%",
    },
  ];

  if (props.platform === "discord") {
    return (
      <div>
        <Card
          style={{ marginBottom: 40 }}
          className="card-popular"
          title="Popular Discord threads"
          bordered={false}
          extra={
            <a id="view-more-popular" href="#" onClick={showPopularModal}>
              View more
            </a>
          }
        >
          <Spin spinning={loading}>
            <List
              className="popular-threads"
              style={{ height: "400px" }}
              id="popular-threads"
              dataSource={popularPosts.slice(0, 5)}
              renderItem={(item) => (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <p style={{ color: "grey" }}>{item.posted}</p>
                      <p
                        style={{
                          color: "black",
                          fontWeight: "bold",
                          marginLeft: "6px",
                        }}
                      >
                        {item.channelName}
                      </p>
                    </div>
                    <p>{item.name}</p>
                  </div>
                  <div className="popular-thread-replies">
                    <div className="flex flex-row justify-center space-x-2">
                      <div>{item.thread_count} </div>
                      <div className="-mt-0.5">
                        <CommentOutlined />
                      </div>{" "}
                    </div>
                    <div className="flex flex-row justify-center space-x-2">
                      <div>{item.member_count} </div>
                      <div className="-mt-0.5">
                        <UserOutlined />
                      </div>{" "}
                    </div>
                  </div>
                </div>
              )}
            />
          </Spin>
        </Card>
        <Modal
          width={1000}
          title="Popular threads"
          style={{ top: 40, right: 10 }}
          visible={isPopularModalVisible}
          onCancel={hidePopularModal}
          centered
          footer={[]}
        >
          <Row justify="spaced-around">
            <div style={{ width: "100%", height: "100%" }}>
              {hasMessagesSelected ? (
                <div>
                  <p>
                    Selected {selectedMessages.length} threads.{" "}
                    <a href="#" onClick={copyThreads}>
                      {" "}
                      Click to copy
                    </a>
                  </p>
                </div>
              ) : (
                <div className="flex gap-3 items-center mb-2">
                  <p className="m-0">You can select threads to copy!</p>
                  <Tooltip title="We limit messages to 30 days to comply with Discord policies">
                    <InfoCircleFilled className="text-base opacity-70" />
                  </Tooltip>
                </div>
              )}
              <Table
                rowSelection={rowSelection}
                dataSource={popularPosts}
                columns={popularThreadsColumns}
                style={{}}
              />
            </div>
          </Row>
        </Modal>
      </div>
    );
  }

  return (
    <div>
      <Card
        style={{ marginBottom: 40 }}
        className="card-popular"
        title="Popular threads"
        bordered={false}
        extra={
          <a id="view-more-popular" href="#" onClick={showPopularModal}>
            View more
          </a>
        }
      >
        <Spin spinning={loading}>
          <List
            className="popular-threads"
            id="popular-posts"
            dataSource={popularPosts.slice(0, 5)}
            renderItem={(item) => (
              <List.Item>
                <List.Item.Meta
                  avatar={
                    <div>
                      <Avatar
                        src={item.slack_user ? item.slack_user.image_32 : ""}
                      />
                      <div className="flex mt-2 justify-center">
                        <Tooltip title="Click to open in slack">
                          <a
                            href={item.permalink}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <img
                              alt="link"
                              width="15"
                              src="/external-link.svg"
                            />
                          </a>
                        </Tooltip>
                      </div>
                    </div>
                  }
                  title={
                    <p>
                      <span>
                        <a href="#">
                          {item.slack_user ? item.slack_user.name : ""}
                        </a>{" "}
                        in #{item.channel.name}
                      </span>
                      <span
                        style={{
                          color: "grey",
                          marginLeft: "10px",
                          marginRight: "10px",
                        }}
                      >
                        {item.posted}
                      </span>
                    </p>
                  }
                  description={
                    <Tooltip title="Click to open in slack">
                      <a
                        style={{ color: "#676868", lineHeight: "12px" }}
                        href={item.permalink}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Emoji text={item.text} />
                      </a>
                    </Tooltip>
                  }
                />
                <div className="popular-thread-replies">
                  <div className="flex flex-row justify-center space-x-2">
                    <div>{item.reply_count} </div>
                    <div className="-mt-0.5">
                      <CommentOutlined />
                    </div>{" "}
                  </div>
                  <div className="flex flex-row justify-center space-x-2">
                    <div>{item.reaction_count} </div>
                    <div className="-mt-0.5">
                      <SmileOutlined />
                    </div>{" "}
                  </div>
                </div>
              </List.Item>
            )}
          />
        </Spin>
      </Card>
      <Modal
        width={1000}
        title="Popular messages"
        style={{ top: 40, right: 10 }}
        visible={isPopularModalVisible}
        onCancel={hidePopularModal}
        footer={[]}
      >
        <Row justify="spaced-around">
          <div style={{ width: "100%", height: "100%" }}>
            {hasMessagesSelected ? (
              <div>
                <p>
                  Selected {selectedMessages.length} messages.{" "}
                  <a href="#" onClick={copyMessages}>
                    {" "}
                    Click to copy
                  </a>
                </p>
              </div>
            ) : (
              <p>You can select messages to copy!</p>
            )}
            <Table
              rowSelection={rowSelection}
              dataSource={popularPosts}
              columns={popularMessagesColumns}
              style={{}}
            />
          </div>
        </Row>
      </Modal>
    </div>
  );
};

export default TopPosts;
